/**
 * Edit Referral Modal - Trade Loop v2.0
 *
 * Allows referral senders to edit their sent referrals
 * Editable fields: client info, job details, location, trades, urgency level
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  User,
  Mail,
  Phone,
  FileText,
  MapPin,
  Clock,
  Zap,
  Check,
  ChevronDown,
  X,
  Loader2,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/contexts/ToastContext";
import { useTradeSections } from "@/hooks/useTrades";
import CityStateAutocomplete from "@/components/ui/CityStateAutocomplete";
import { LocationResult } from "@/lib/location-data";
import { PhoneInput } from "@/components/ui/phone-input";

interface EditReferralModalProps {
  referral: {
    id: number;
    clientName?: string;
    clientEmail?: string;
    clientPhone?: string;
    jobDescription?: string;
    jobLocation?: string;
    trades?: string[];
    customTrades?: string[];
    urgencyLevel?: string;
    urgencyLevelId?: number;
    [key: string]: any;
  };
  isOpen: boolean;
  onClose: () => void;
}

export default function EditReferralModal({
  referral,
  isOpen,
  onClose,
}: EditReferralModalProps) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const { data: tradeSections = [], isLoading: tradesLoading } = useTradeSections();

  // Form state
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [cityStateInput, setCityStateInput] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<{
    city: string;
    state: string;
  } | null>(null);
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [customTrades, setCustomTrades] = useState<string[]>([]);
  const [urgencyLevel, setUrgencyLevel] = useState<"low" | "normal" | "urgent">("normal");

  // Initialize form with referral data
  useEffect(() => {
    if (referral && isOpen) {
      setClientName(referral.clientName || "");
      setClientEmail(referral.clientEmail || "");
      setClientPhone(referral.clientPhone || "");
      setJobDescription(referral.jobDescription || "");
      setSelectedTrades(referral.trades || []);
      setCustomTrades(referral.customTrades || []);

      // Parse job location. Backend stores a single job_location text column
      // that senders pack as either "city, state" or "street, city, state"
      // (street itself may contain commas, e.g. "Suite 100, 123 Main St, Denver, CO").
      // Treat the last two parts as city/state and everything before as street.
      if (referral.jobLocation) {
        const parts = referral.jobLocation.split(", ");
        if (parts.length === 2) {
          setStreetAddress("");
          setSelectedLocation({
            city: parts[0],
            state: parts[1],
          });
        } else if (parts.length >= 3) {
          const state = parts[parts.length - 1];
          const city = parts[parts.length - 2];
          const street = parts.slice(0, parts.length - 2).join(", ");
          setStreetAddress(street);
          setSelectedLocation({ city, state });
        } else {
          setStreetAddress("");
        }
      } else {
        setStreetAddress("");
      }

      // Set urgency level
      if (referral.urgencyLevel) {
        setUrgencyLevel(referral.urgencyLevel.toLowerCase() as "low" | "normal" | "urgent");
      }
    }
  }, [referral, isOpen]);

  const toggleTrade = (trade: string) => {
    setSelectedTrades((prev) => {
      const isRemoving = prev.includes(trade);
      const newTrades = isRemoving
        ? prev.filter((t) => t !== trade)
        : [...prev, trade];

      // Clear custom trades when "Other" is deselected
      if (trade === 'Other' && isRemoving) {
        setCustomTrades([]);
      }

      return newTrades;
    });
  };

  const updateReferralMutation = useMutation({
    mutationFn: async (data: any) => {
      // Use custom notification route that properly handles trades via junction table
      const response = await fetch(`/api/referrals-update-with-notification/custom/${referral.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update referral");
      }

      return response.json();
    },
    onSuccess: () => {
      showSuccess("Referral Updated", "Your referral has been updated successfully");
      // Invalidate all referral-related queries to ensure immediate UI updates
      queryClient.invalidateQueries({ queryKey: ["/api/referrals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      onClose();
    },
    onError: (error: Error) => {
      showError("Update Failed", error.message || "Failed to update referral. Please try again.");
    },
  });

  const handleSubmit = async () => {
    // Validation
    if (!clientName.trim()) {
      showError("Required Field", "Client name is required");
      return;
    }
    if (!jobDescription.trim()) {
      showError("Required Field", "Job description is required");
      return;
    }
    if (!selectedLocation) {
      showError("Required Field", "Job location is required");
      return;
    }

    // Map urgency level to ID
    const urgencyLevelMap: Record<string, number> = {
      low: 1,
      normal: 2,
      urgent: 3,
    };

    // Filter out empty custom trades
    const validCustomTrades = customTrades.filter(t => t.trim() !== '');

    // Backend has no street_address column — pack street into job_location
    // the same way SendReferralPage does on create.
    const trimmedStreet = streetAddress.trim();
    const jobLocation = trimmedStreet
      ? `${trimmedStreet}, ${selectedLocation.city}, ${selectedLocation.state}`
      : `${selectedLocation.city}, ${selectedLocation.state}`;

    const updateData = {
      client_name: clientName.trim(),
      client_email: clientEmail.trim() || null,
      client_phone: clientPhone.trim() || null,
      job_description: jobDescription.trim(),
      job_location: jobLocation,
      trades: selectedTrades,
      custom_trades: validCustomTrades.length > 0 ? validCustomTrades : null,
      urgency_level_id: urgencyLevelMap[urgencyLevel],
      updated_at: new Date().toISOString(),
    };

    updateReferralMutation.mutate(updateData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Referral</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Client Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              Client Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
            />
          </div>

          {/* Client Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              Client Email <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <Input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="client@example.com"
            />
          </div>

          {/* Client Phone */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              Client Phone <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <PhoneInput
              value={clientPhone}
              onChange={setClientPhone}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Job Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Job Description <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Describe the job or project..."
              className="h-[3.5rem] resize-none overflow-y-auto"
              rows={2}
            />
          </div>

          {/* Trades Multi-Select Dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Trade(s)</label>
            <p className="text-xs text-gray-500">Select the trades this job relates to</p>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between h-auto min-h-[40px] text-left font-normal"
                >
                  <span className="text-sm text-gray-500">
                    {selectedTrades.length > 0
                      ? `${selectedTrades.length} selected`
                      : "Select trades..."}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command
                  filter={(value, search) => {
                    if (!search) return 1;
                    return value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0;
                  }}
                >
                  <CommandInput placeholder="Search trades..." className="h-9" />
                  <CommandList className="max-h-64 overflow-auto">
                    <CommandEmpty>No trade found.</CommandEmpty>
                    {/* Other/Custom option at the top */}
                    <CommandGroup>
                      <CommandItem
                        key="__OTHER__"
                        value="Other Custom"
                        onSelect={() => toggleTrade('Other')}
                        className="border-b border-gray-100"
                      >
                        <Checkbox
                          checked={selectedTrades.includes('Other')}
                          className="mr-2"
                        />
                        <span className="flex-1 font-medium text-purple-700">Other</span>
                        {selectedTrades.includes('Other') && (
                          <Check className="ml-2 h-4 w-4 text-purple-600" />
                        )}
                      </CommandItem>
                    </CommandGroup>
                    {tradesLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-sm text-gray-500">Loading trades...</span>
                      </div>
                    ) : (
                      tradeSections.map((section) => (
                        <CommandGroup key={section.section || 'Other'} heading={section.section || 'Other'}>
                          {section.trades.map((trade) => (
                            <CommandItem
                              key={trade.id}
                              value={trade.name}
                              onSelect={() => toggleTrade(trade.name)}
                            >
                              <Checkbox
                                checked={selectedTrades.includes(trade.name)}
                                className="mr-2"
                              />
                              <span className="flex-1">{trade.name}</span>
                              {selectedTrades.includes(trade.name) && (
                                <Check className="ml-2 h-4 w-4 text-blue-600" />
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedTrades.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedTrades.map((trade) => (
                  <span
                    key={trade}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                      trade === 'Other'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {trade}
                    <button
                      type="button"
                      onClick={() => toggleTrade(trade)}
                      className={`rounded-full p-0.5 ${
                        trade === 'Other' ? 'hover:bg-purple-200' : 'hover:bg-blue-200'
                      }`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Custom Trades - Only show when "Other" is selected */}
          {selectedTrades.includes('Other') && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Custom Trades</label>
            <p className="text-xs text-gray-500">Add any custom trades not in the list above (up to 3)</p>
            {customTrades.map((trade, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={trade}
                  onChange={(e) => {
                    const updated = [...customTrades];
                    updated[index] = e.target.value;
                    setCustomTrades(updated);
                  }}
                  placeholder={`Custom trade ${index + 1}`}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const updated = customTrades.filter((_, i) => i !== index);
                    setCustomTrades(updated);
                  }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {customTrades.length < 3 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCustomTrades([...customTrades, ""])}
                className="text-sm"
              >
                + Add Custom Trade
              </Button>
            )}
          </div>
          )}

          {/* Urgency Level Dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              Urgency Level
            </label>
            <p className="text-xs text-gray-500">Let your partner know how time-sensitive this job is</p>
            <Select value={urgencyLevel} onValueChange={(value: "low" | "normal" | "urgent") => setUrgencyLevel(value)}>
              <SelectTrigger className="w-full">
                <SelectValue>
                  <span className="flex items-center gap-2">
                    {urgencyLevel === "low" && <Clock className="w-4 h-4 text-gray-600" />}
                    {urgencyLevel === "normal" && <Clock className="w-4 h-4 text-blue-600" />}
                    {urgencyLevel === "urgent" && <Zap className="w-4 h-4 text-red-600" />}
                    <span className="text-sm">
                      {urgencyLevel === "low" && "Low"}
                      {urgencyLevel === "normal" && "Normal"}
                      {urgencyLevel === "urgent" && "Urgent"}
                    </span>
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-600" />
                    Low
                  </span>
                </SelectItem>
                <SelectItem value="normal">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Normal
                  </span>
                </SelectItem>
                <SelectItem value="urgent">
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-red-600" />
                    Urgent
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Street Address */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              Street Address <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <Input
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              placeholder="123 Main Street"
            />
          </div>

          {/* City, State */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              City, State <span className="text-red-500">*</span>
            </label>
            <CityStateAutocomplete
              value={cityStateInput}
              onChange={setCityStateInput}
              onSelect={(location: LocationResult) => {
                const newLocation = {
                  city: location.city,
                  state: location.stateCode,
                };
                setSelectedLocation(newLocation);
                setCityStateInput("");
              }}
              placeholder="Start typing a city..."
              className="w-full"
              disabled={selectedLocation !== null}
            />

            {/* Location Badge */}
            {selectedLocation && (
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full">
                  <MapPin className="w-3 h-3" />
                  {selectedLocation.state
                    ? `${selectedLocation.city}, ${selectedLocation.state}`
                    : selectedLocation.city}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLocation(null);
                      setCityStateInput("");
                    }}
                    className="hover:bg-indigo-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={updateReferralMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateReferralMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {updateReferralMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
